/* eslint-disable react/display-name */
import React, { useCallback, useEffect, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import { Button, Form, Image, Input, Space, Tag, Typography } from 'antd';
import { CloseOutlined, PictureOutlined, PlusOutlined } from '@ant-design/icons';
import LoaderWrapper from 'src/components/atoms/LoaderWrapper';
import { Link, useHistory, useParams } from 'react-router-dom';
import tileStyles from 'src/styles/tile.module.css';
import { AppDispatch } from 'src/index';
import { useDispatch } from 'react-redux';
import { CompanyBannerType } from 'src/features/companyBanners/companyBanners.types';
import { useForm } from 'antd/es/form/Form';
import { useCompanyVideos, useCompanyVideosState } from 'src/features/companyVideos/companyVideos.hooks';
import { createCompanyVideo, fetchCompanyVideos } from 'src/features/companyVideos/companyVideos.state';
import { NewCompanyVideo } from 'src/features/companyVideos/companyVideos.api';
import { RoleEnum } from 'src/features/users/users.types';
import ComponentRoleProtected from 'src/components/blocks/ComponentRoleProtected';

type NewBanner = { name: string };

interface CreateBannerProps {
  onCreate: (req: NewBanner) => void;
  onCancel: () => void;
}

const CreateBannerForm: React.FC<CreateBannerProps> = props => {
  const { onCreate, onCancel } = props;
  const [form] = useForm();

  const onFinish = (values: NewBanner) => onCreate(values);

  return (
    <Form
      form={form}
      layout={'vertical'}
      onFinish={onFinish}
      initialValues={{ bannerType: CompanyBannerType.AdBanner }}
    >
      <Form.Item name='name' label={'Описание'} required>
        <Input type='text' name='name' placeholder='Введите описание' autoFocus />
      </Form.Item>
      <Form.Item shouldUpdate>
        {() => (
          <Space>
            <Button
              type='primary'
              htmlType='submit'
              disabled={
                !form.isFieldsTouched(['name']) || !!form.getFieldsError().filter(({ errors }) => errors.length).length
              }
            >
              Submit
            </Button>
            <Button type={'dashed'} onClick={onCancel}>
              Cancel
            </Button>
          </Space>
        )}
      </Form.Item>
    </Form>
  );
};

const CompanyVideosTab: React.FC = () => {
  const { companyId } = useParams() as { companyId: string };
  const [createMode, setCreateMode] = useState<boolean>(false);
  const dispatch: AppDispatch = useDispatch();
  const videos = useCompanyVideos();
  const state = useCompanyVideosState();
  const [search, setSearch] = useState<string | undefined>(undefined);
  const history = useHistory();

  useEffect(() => {
    dispatch(fetchCompanyVideos({ companyId }));
  }, [dispatch, companyId]);

  const createHandler = useCallback(
    async (data: NewCompanyVideo) => {
      const response = await dispatch(
        createCompanyVideo({
          companyId,
          data,
        })
      );
      if (createCompanyVideo.fulfilled.match(response)) {
        setCreateMode(false);
        history.push(`/commercial/companies/${companyId}/videos/${response.payload.id}`);
      }
    },
    [dispatch, companyId, history]
  );

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <Typography.Title level={4}>Промо видео</Typography.Title>
        <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
          {createMode ? (
            <Button onClick={() => setCreateMode(false)} icon={<CloseOutlined />} />
          ) : (
            <Button onClick={() => setCreateMode(true)} icon={<PlusOutlined />} />
          )}
        </ComponentRoleProtected>
      </div>
      {!createMode && (
        <div className={styles.searchWrap}>
          <Input
            type='text'
            value={search}
            placeholder='Введите название'
            style={{ marginRight: 8 }}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
      )}
      <div className={styles.content}>
        {createMode && (
          <ComponentRoleProtected roles={[RoleEnum.admin, RoleEnum.companyContentManager]}>
            <CreateBannerForm onCreate={createHandler} onCancel={() => setCreateMode(false)} />
          </ComponentRoleProtected>
        )}
        <LoaderWrapper loading={state.isPending}>
          {videos.map(video => (
            <Link
              key={video.id}
              className={tileStyles.listTile}
              style={{ height: '85px' }}
              to={`/commercial/companies/${companyId}/videos/${video.id}`}
            >
              <Space direction={'vertical'} size={1}>
                <Space>
                  <PictureOutlined />
                  <span>{video.name}</span>
                </Space>
              </Space>
              <Space align='center'>
                {!video.image ? (
                  <Tag color={'red'}>Пустой</Tag>
                ) : (
                  <Space>
                    <div
                      style={{
                        width: 60,
                        height: 60,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Image src={video.image} width={'100%'} preview={false} />
                    </div>
                  </Space>
                )}
              </Space>
            </Link>
          ))}
        </LoaderWrapper>
      </div>
    </div>
  );
};

export default CompanyVideosTab;
